import { clientConfig } from '../config/clientConfig';
import type { FormData } from '../types/form';

const STORAGE_KEY = 'marketing_params';
const EXPIRY_KEY = 'marketing_params_expiry';
const EXPIRY_HOURS = 24;
import { campaignVariantService } from '../services/campaignVariants';
import { formDefaultsService } from '../services/formDefaults';

export class MarketingParamsManager {
  private static instance: MarketingParamsManager;
  private params: Record<string, string> = {};
  private initialized: boolean = false;

  // Marketing tracking parameters definition
  private static readonly MARKETING_PARAMS = {
    'lead_source_code__c': '',
    // Campaign Variant Parameters
    'variant': '',
    'campaign_variant': '',
    'offer_code': '',
    'first_name': '',
    'offer_amount': '',
    'offer_type': '',
    // Google Ads
    'gclid': '',
    'utm_feeditemid': '',
    'utm_source': '',
    'utm_medium': '',
    'utm_campaign': '',
    'utm_term': '',
    'utm_content': '',
    'utm_device': '',
    'gad_source': '',
    // Meta (Facebook)
    'fbclid': '',
    'fb_source': '',
    // TikTok
    'ttclid': '',
    // Microsoft Ads
    'msclkid': '',
    // Generic
    'source': '',
    'medium': '',
    'campaign': '',
    'term': '',
    'content': '',
    'referrer': '',
    'landing_page': '',
    // HubSpot
    'hsa_cam': '',
    'hsa_grp': '',
    'hsa_mt': '',
    'hsa_src': '',
    'hsa_ad': '',
    'hsa_acc': '',
    'hsa_net': '',
    'hsa_kw': '',
    'hsa_tgt': '',
    'hsa_ver': '',
    // Custom
    'affiliate_id': '',
    'partner_id': '',
    'sub_id': '',
    'placement': '',
    'creative': '',
    'device': '',
    'matchtype': '',
    'network': '',
    'target': '',
    'position': '',
    'loc_physical_ms': '',
    'loc_interest_ms': '',
    'platform': '',
    // Disclosed  
    'loan_amount': '0',
    'loan_purpose': '',
    'property_status': '',
    'employment_status': '',
    'employment_pay_frequency': '',
    'annual_income__c': '0',
    'engine_annual_income': '',
    'education_level': '',
    // Partners
    'bpo': '',
    'partner_code': '',
    'partner_name': ''
  } as const;

  private constructor() {
    this.loadFromStorage();
  }

  public static getInstance(): MarketingParamsManager {
    if (!MarketingParamsManager.instance) {
      MarketingParamsManager.instance = new MarketingParamsManager();
    }
    return MarketingParamsManager.instance;
  }

  private isStorageExpired(): boolean {
    const expiryTime = sessionStorage.getItem(EXPIRY_KEY);
    if (!expiryTime) return true;
    return Date.now() > parseInt(expiryTime, 10);
  }

  private setStorageExpiry(): void {
    const expiryTime = Date.now() + (EXPIRY_HOURS * 60 * 60 * 1000);
    sessionStorage.setItem(EXPIRY_KEY, expiryTime.toString());
  }

  private loadFromStorage(): void {
    // Check if we have valid stored params
    const storedParams = sessionStorage.getItem(STORAGE_KEY);
    
    if (storedParams && !this.isStorageExpired()) {
      this.params = JSON.parse(storedParams);
    } else {
      this.params = { ...MarketingParamsManager.MARKETING_PARAMS };
      // Don't save to storage yet - wait for URL parameters
    }
  }

  private saveToStorage(): void {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.params));
    this.setStorageExpiry();
  }

  public initializeFromUrl(): void {
    if (this.initialized) return;

    const urlParams = new URLSearchParams(window.location.search);
    let hasNewParams = false;

    // Collect all marketing parameters from URL
    Object.keys(MarketingParamsManager.MARKETING_PARAMS).forEach(param => {
      const value = urlParams.get(param);
      if (value && (!this.params[param] || this.params[param] === '')) {
        this.params[param] = value;
        hasNewParams = true;
      }
    });

    // Set domain type if not already set
    if (!this.params.lead_source_code__c) {
      this.params.lead_source_code__c = clientConfig.environmentName;
      hasNewParams = true;
    }
    
    // Add referrer and landing page if not already set
    if (!this.params.referrer) {
      this.params.referrer = document.referrer;
      hasNewParams = true;
    }
    if (!this.params.landing_page) {
      this.params.landing_page = window.location.pathname + window.location.search;
      hasNewParams = true;
    }

    // Add device and platform info if not already set
    if (!this.params.device) {
      this.params.device = /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(navigator.userAgent) 
        ? 'mobile' 
        : 'desktop';
      hasNewParams = true;
    }
    if (!this.params.platform) {
      this.params.platform = navigator.platform;
      hasNewParams = true;
    }

    // Initialize campaign variant service
    const variant = campaignVariantService.initializeFromUrl();
    const variantAnalytics = campaignVariantService.getAnalyticsData();
    
    // Add variant analytics to marketing params
    Object.entries(variantAnalytics).forEach(([key, value]) => {
      if (!this.params[key]) {
        this.params[key] = value;
        hasNewParams = true;
      }
    });
    
    // Add form defaults analytics to marketing params
    const defaultsAnalytics = formDefaultsService.getAnalyticsData();
    Object.entries(defaultsAnalytics).forEach(([key, value]) => {
      if (!this.params[key]) {
        this.params[key] = value;
        hasNewParams = true;
      }
    });

    // Only save if we have new parameters
    if (hasNewParams) {
      this.saveToStorage();
    }

    this.initialized = true;
  }

  public updateFormData(formData: Partial<FormData>): void {
    let hasUpdates = false;

    // Only update if the new value is not null/undefined/empty
    if (formData.loanAmount && formData.loanAmount > 0) {
      this.params.loan_amount = formData.loanAmount.toString();
      this.params.engine_annual_income = formData.loanAmount.toString();
      hasUpdates = true;
    }
    if (formData.loanPurpose) {
      this.params.loan_purpose = formData.loanPurpose;
      hasUpdates = true;
    }
    if (formData.propertyStatus) {
      this.params.property_status = formData.propertyStatus;
      hasUpdates = true;
    }
    if (formData.employmentStatus) {
      this.params.employment_status = formData.employmentStatus;
      hasUpdates = true;
    }
    if (formData.employmentFrequency) {
      this.params.employment_pay_frequency = formData.employmentFrequency;
      hasUpdates = true;
    }
    if (formData.annualIncome && formData.annualIncome > 0) {
      this.params.annual_income__c = formData.annualIncome.toString();
      this.params.engine_annual_income = formData.annualIncome.toString();
      hasUpdates = true;
    }
    if (formData.educationLevel) {
      this.params.education_level = formData.educationLevel;
      hasUpdates = true;
    }

    // Only save to storage if we actually updated any values
    if (hasUpdates) {
      this.saveToStorage();
    }
  }

  public getParams(): Record<string, string> {
    return { ...this.params };
  }

  public clearStorage(): void {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(EXPIRY_KEY);
    this.params = { ...MarketingParamsManager.MARKETING_PARAMS };
    this.initialized = false;
    this.initializeFromUrl();
  }
}

// Export singleton instance
export const marketingParams = MarketingParamsManager.getInstance();